
import { graph_utils } from './graph_utils';
import indexeddb_wrapper from './indexeddb_wrapper';
import { SvelteMap, SvelteSet } from 'svelte/reactivity';

const is_browser = typeof window !== 'undefined';

type Function<T> = () => T | null | Promise<T | null>;
type IndexKeys<T> = { last_updated_index: keyof T; main_index: keyof T };
type FilteringFn<T> = (item: T) => boolean;

type PaginatedT<T> = T & { $order_number: number };

export type PaginatedState<T> = {
   search: {
      start: (search_fn: FilteringFn<T>, sort_direction?: 'asc' | 'desc') => Promise<void>;
      cancel: () => void;
      goto_page: (requested_page: number) => Promise<void>;
      next: () => Promise<void>;
      prev: () => Promise<void>;
      refresh: () => Promise<void>;
      readonly is_active: boolean;
      readonly search_fn: FilteringFn<T> | undefined
   };
   pagination: {
      start: (page?: number, size?: number, filter_fn?: FilteringFn<T>, sort_direction?: 'asc' | 'desc') => Promise<void>;
      cancel: () => void;
      next: () => Promise<void>;
      current: () => Promise<void>;
      goto_page: (page: number) => Promise<void>;
      prev: () => Promise<void>;
   };
   set_global_filter(filter_fn: FilteringFn<T> | undefined): void;
   get_effective_filter_fn: () => FilteringFn<T> | undefined;
   refresh: () => void;
   readonly page: number;
   page_size: number;
   readonly values: PaginatedT<T>[];
   readonly count: number;
}

type StateInput<T extends { id: string }> = {
   populate_fn?: Function<T[]>;
   incremental?: boolean;
   state_name?: string;
   index_keys?: IndexKeys<T>;
   fetch_one_fn?: (id: string) => Promise<T | null>;
   derive_value_fn?: (value: T) => T;
   db_name?: string;
};

export type ItemSubSingle<T> = {
   readonly value: T | null;
   refresh(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: (keyof T)[] | undefined): void
   async_value_promise: Promise<T | null>
   unsubscribe: () => void;
}

export type ItemsSub<T> = {
   readonly map: SvelteMap<string, T>;
   readonly values: T[];
   refresh: (filter_fn: (item: T) => boolean, return_fields?: (keyof T)[] | undefined) => void
   async_values_promise: Promise<T[]>
   unsubscribe: () => void;
}

export type MapStateReturn<T> = {
   map: SvelteMap<string, T>;
   count: number;
   get_stored_values: () => Promise<T[] | null>;
   populate_fn: () => Promise<T[] | null>;
   reset: () => void;
   on_updates: (fn: (values: T[]) => any) => (() => void)
   on_deletes: (fn: (ids: string[]) => any) => (() => void)
   after_updates: (fn: (values: T[]) => any) => (() => void)
   on_update_single: (fn: (value: T) => any) => (() => void)
   on_delete_single: (fn: (id: string) => any) => (() => void)
   after_update_single: (fn: (value: T) => any) => (() => void)

   init: () => void;
   resync_state: () => Promise<void>;
   update_many: (array: T[]) => Promise<void>;
   update_one: (value: T) => Promise<void>;
   remove_one: (id: string) => Promise<boolean>;
   remove_many: (id: string[]) => Promise<boolean>;
   paginated_state: PaginatedState<T>;
   filter_async: (filter_fn?: (value: T) => boolean, return_fields?: Array<keyof T>) => Promise<T[]>;

   items_with_sub: (filter_fn?: (item: T) => boolean, return_fields?: Array<keyof T>) => ItemsSub<T>
   items_with_no_sub: (filter_fn?: (item: T) => boolean, return_fields?: Array<keyof T>) => { readonly values: T[]; async_values_promise: Promise<T[]>; };
   item_with_sub_single(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: (keyof T)[] | undefined): ItemSubSingle<T>;
   item_with_no_sub_single(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: (keyof T)[] | undefined): { readonly value: T | null; async_value_promise: Promise<T | null>; };
   Type: T;
   get_or_fetch_one: (id: string) => Promise<T | null>;
   state_name: string;
   PaginatedType: PaginatedT<T>;
   get_state_latest_sync: () => Promise<string>,
   indexeddb_crud: {
      get_one: (id: string) => Promise<T | null>;
      get_all: () => Promise<T[] | null>;
      keys: () => Promise<string[]>;
      set_one: (value: T) => Promise<void>;
      set_bulk: (array: T[]) => Promise<void>;
      clear_store: () => Promise<boolean>;
      remove_one: (id: string) => Promise<void>;
      count_items_after_key: (filter_fn: (item: T) => boolean, index_keys: keyof T, target_key: IDBValidKey, index_to_use?: keyof T) => Promise<number>
      paginated_filter: (options: {
         page: number;
         page_size: number;
         filter_fn: (item: T) => boolean;
         index_keys: keyof T;
         index_to_use?: Extract<keyof T, string>;
         cursor_key?: IDBValidKey;
      }) => Promise<[values: PaginatedT<T>[], first_index_key: IDBValidKey | undefined, last_index_key: IDBValidKey | undefined, has_more: boolean]>

      paginated_filter_count: (filter_fn: (value: T) => boolean) => Promise<number>;
      filter_count: (filter_fn: (value: T) => boolean) => Promise<number>;

      filter: (filter_fn: (value: T) => boolean) => Promise<T[]>;
      paginate: (
         page: number,
         page_size: number,
         index_to_use: keyof T
      ) => Promise<(T & { $order_number: number })[]>;
      get_last_key_value: () => Promise<T | null>;
      get_last_updated_key_value: () => Promise<T | null>;
      count: () => Promise<number>;
   };
};

// let is_device_capable = true;

export function create_map_state<T extends { id: string }>({
   state_name = '',
   db_name,
   populate_fn,
   index_keys,
   incremental,
   fetch_one_fn,
   derive_value_fn
}: StateInput<T>): MapStateReturn<T> {
   let paginated_items_state: PaginatedT<T>[] = $state([]);
   let paginated_state: PaginatedState<T> | null = $state(null);
   let state_count = $state(0);
   let is_pagination_active = $state(false);
   const filter_subs_map = new Map<string, () => void>()

   const on_updates_set = new SvelteSet<(values: T[]) => void>();
   const on_update_single_set = new SvelteSet<(value: T) => void>();
   const on_deletes_set = new SvelteSet<(ids: string[]) => void>();
   const on_delete_single_set = new SvelteSet<(id: string) => void>();
   const after_updates_set = new SvelteSet<(values: T[]) => void>();
   const after_update_single_set = new SvelteSet<(value: T) => void>();

   async function item_fetch_fn(id: string, value: T | null): Promise<void> {
      // console.log('item_fetch_fn', { id, state_name });
      if (value?.id) return;
      const document = (await indexeddb_crud.get_one(id)) as T | null;
      if (!document?.id) {
         if (fetch_one_fn)
            fetch_one_fn(id)
               .then((fetched_value) => {
                  // console.log({ fetched_value })
                  if (fetched_value?.id) current_map_state.set(fetched_value.id, fetched_value);
               })
               .catch((error) =>
                  console.error('error at item_fetch_fn - fetch_one_fn: ' + state_name, { error })
               );
         return;
      }
      current_map_state.set(document.id, document);
   }

   async function get_or_fetch_one(id: string): Promise<T | null> {
      const document = (await indexeddb_crud.get_one(id)) as T | null;
      if (document) return document
      if (!fetch_one_fn) return null

      const fetched_value = await fetch_one_fn(id)
      if (!fetched_value) return null

      return fetched_value
   }

   const map_internal = new SvelteMap<string, T>();

   const current_map_state = {
      set: (key: string, value: T, derived_value?: T) => {
         map_internal.set(key, derived_value ?? value)
         indexeddb_crud.set_one($state.snapshot(value ?? derived_value) as T);
         // return map_internal.set.bind(map_internal)
      },
      delete: (key: string) => {
         map_internal.delete(key)
      },
      has: map_internal.has.bind(map_internal),
      clear: map_internal.clear.bind(map_internal),
      entries: map_internal.entries.bind(map_internal),
      keys: map_internal.keys.bind(map_internal),
      values: map_internal.values.bind(map_internal),
      forEach: map_internal.forEach.bind(map_internal),
      get size() {
         return map_internal.size;
      },

      get: (key: string): T | null => {
         // Run the external function on each 'get' operation
         const value = map_internal.get.bind(map_internal)(key) ?? null;
         if (is_browser && key) item_fetch_fn(key, value);
         // Call the original 'get' method
         if (derive_value_fn && value) return derive_value_fn(value) ?? null;
         else return value ?? null;
      }
   };

   if (!is_browser)
      return {
         map: current_map_state,
         get_stored_value: async () => null as T[] | null,
         populate_fn: async () => {
            return;
         },
         paginated_state: async () => {
            return {
               next: () => { },
               prev: () => { },
               paginated: []
            };
         }
      } as unknown as MapStateReturn<T>;

   async function get_stored_values(): Promise<T[] | null> {
      try {
         if (!state_name || !db_name) return null;
         // if (!is_device_capable) return [] as T[]

         const results = await indexeddb_crud.get_all();
         return results;
      } catch (error) {
         console.error(`Error at get_value function, state: ${state_name}.`, { error });
         return null;
      }
   }

   async function set_state_latest_sync(updates: T[]) {

      if (typeof window === 'undefined' || !updates.length || !(index_keys?.last_updated_index! in updates?.[0])) return false
      const latest_updated_date = graph_utils.latest_date_string_in_array(updates, index_keys?.last_updated_index!)
      // console.log({ latest_updated_date, updates })
      localStorage.setItem(state_name + '_latest_sync', `${latest_updated_date}|${await indexeddb_crud.count()}`)
      return true;
   }


   async function get_state_latest_sync() {

      if (typeof window === 'undefined') return new Date(0).toISOString();

      const latest_sync_string = localStorage.getItem(state_name + '_latest_sync');
      let [latest_sync, latest_count] = latest_sync_string?.split('|') ?? [new Date(0).toISOString(), '0'];
      const current_count = await indexeddb_crud.count()

      if (!latest_count || !(+latest_count) || +latest_count > current_count) latest_sync = new Date(0).toISOString();

      // console.log({ key: state_name + '_latest_sync', latest_sync, current_count, latest_count })

      return latest_sync ?? new Date(0).toISOString();
   }

   async function populate_fn_handler() {
      if (!populate_fn) return null;
      // await await_state_init();
      // console.log('popolating ', state_name)
      const values = await populate_fn();
      if (!values) return null;

      if (incremental) await set_state_latest_sync(values)
      return values;
   }

   async function update_map_values(array: T[]) {

      const cloned_array = $state.snapshot(array);
      if (!incremental) {
         for (const object of array) {
            if (!object?.id) continue;
            map_internal.set(object.id, derive_value_fn ? derive_value_fn(object) : object)
         }
      }

      if (on_update_single_set.size) {
         for (const sub_fn of on_update_single_set) {
            array.forEach((value) => sub_fn(value));
         }
      }
      if (on_updates_set.size) {
         for (const sub_fn of on_updates_set) {
            sub_fn(array)
         }
      }

      await indexeddb_crud.set_bulk(cloned_array as T[]);

      if (after_update_single_set.size) {
         for (const sub_fn of after_update_single_set) {
            array.forEach((value) => sub_fn(value));
         }
      }

      if (after_updates_set.size) {
         for (const sub_fn of after_updates_set) {
            sub_fn(array)
         }
      }


      if (!paginated_state) return;

      const is_search_active_and_array_has_searched_for_items = paginated_state.search.is_active &&
         paginated_state.search.search_fn &&
         array.some((item) => paginated_state?.search.search_fn?.(derive_value_fn ? derive_value_fn(item) : item))

      if (is_search_active_and_array_has_searched_for_items) paginated_state.search.refresh();
      else if (is_pagination_active) paginated_state.pagination.current();

   }

   async function update_map_value_one(value: T) {
      if (!value?.id) return;

      const derived_value = derive_value_fn ? derive_value_fn(value) : value;
      if (!incremental) current_map_state.set(value.id, value, derived_value);
      if (on_updates_set.size) on_updates_set.forEach(sub_fn => sub_fn([value]))
      if (on_update_single_set.size) on_update_single_set.forEach(sub_fn => sub_fn(value))

      await indexeddb_crud.set_one(value);
      if (after_updates_set) after_updates_set.forEach(sub_fn => sub_fn([value]))
      if (after_update_single_set) after_update_single_set.forEach(sub_fn => sub_fn(value))

      if (!paginated_state || !is_pagination_active) return;
      if (
         paginated_state.search.is_active &&
         paginated_state.search.search_fn?.(derived_value)
      ) {
         paginated_state.search.refresh();
      } else if (is_pagination_active) {
         await paginated_state.pagination.current();
      }

   }

   async function remove_one(id: string) {
      if (!id) return false;
      current_map_state.delete(id);

      if (on_deletes_set.size) on_deletes_set.forEach(sub_fn => sub_fn([id]))
      if (on_delete_single_set.size) on_delete_single_set.forEach(sub_fn => sub_fn(id))

      await indexeddb_crud.remove_one(id);
      paginated_state?.pagination.current();

      if (incremental && is_browser) {
         const latest_sync_string = localStorage.getItem(state_name + '_latest_sync');
         let [latest_sync] = latest_sync_string?.split('|') ?? [new Date(0).toISOString(), '0'];

         localStorage.setItem(state_name + '_latest_sync', `${latest_sync}|${await indexeddb_crud.count()}`)
      }

      return true
   }

   async function remove_many(ids: string[]) {
      try {
         if (!ids.length) return false

         ids.forEach(id => current_map_state.delete(id));

         await Promise.all(ids.map(id => indexeddb_crud.remove_one(id)));
         paginated_state?.pagination.current();

         if (is_browser && incremental) {

            const latest_sync_string = localStorage.getItem(state_name + '_latest_sync');
            let [latest_sync] = latest_sync_string?.split('|') ?? [new Date(0).toISOString(), '0'];

            localStorage.setItem(state_name + '_latest_sync', `${latest_sync}|${await indexeddb_crud.count()}`)
         }

         return true
      } catch (error) {
         console.error('Error at remove_many function: ' + state_name, { error });
         return false
      }
   }


   async function resync_state() {
      current_map_state.clear();
      await indexeddb_crud.clear_store();
      state_count = 0;
      paginated_items_state = [];
      filter_subs_map.forEach(unsub => unsub())
      paginated_state?.refresh();
   }


   function reset_state() {
      current_map_state.clear();
      indexeddb_crud.clear_store();
      paginated_items_state = [];
      state_count = 0;
      paginated_state = null;
      if (is_browser) localStorage.removeItem(state_name + '_latest_sync')
   }

   const indexeddb_crud = {
      get_one: async (id: string) => {
         if (!db_name) return null
         return (await indexeddb_wrapper.get_one(db_name, state_name, id, index_keys as any)) ?? null;
      },
      get_last_key_value: async () => {
         if (!db_name) return null
         return indexeddb_wrapper.last_key_value(db_name, state_name, index_keys as any);
      },
      get_last_updated_key_value: async () => {
         if (!db_name) return null
         return indexeddb_wrapper.last_updated_key_value(db_name, state_name, index_keys as any);
      },

      get_all: async () => {
         if (!db_name) return []
         return indexeddb_wrapper.get_all<T>(db_name, state_name, index_keys as any);
      },
      keys: async () => {
         if (!db_name) return null
         return await indexeddb_wrapper.keys(db_name, state_name, index_keys as any);
      },

      set_one: async (value: T) => {
         if (!db_name) return
         return indexeddb_wrapper.set_one(db_name, state_name, value, index_keys as any);
      },
      set_bulk: async (array: T[]) => {
         if (!db_name) return

         return indexeddb_wrapper.set_bulk(db_name, state_name, array, index_keys as any);
      },
      clear_store: async () => {
         if (!db_name) return false
         return indexeddb_wrapper.clear_store(db_name, state_name, index_keys as any);
      },
      paginated_filter_count: async (filter_fn: (value: T) => boolean): Promise<number> => {
         if (!db_name) return 0
         return indexeddb_wrapper.paginated_filter_count(
            db_name,
            state_name,
            filter_fn,
            index_keys as any
         );
      },

      remove_one: async (id: string) => {
         if (!db_name) return
         return indexeddb_wrapper.remove_one(db_name, state_name, id, index_keys as any);
      },
      filter: async (filter_fn: (value: T) => boolean, return_fields?: Array<keyof T>,) => {
         if (!db_name) return [] as T[]
         return indexeddb_wrapper.filter(db_name, state_name, filter_fn, index_keys as any, return_fields);
      },
      filter_count: async (filter_fn: (value: T) => boolean) => {
         if (!db_name) return [] as T[]
         return indexeddb_wrapper.filter_count(db_name, state_name, filter_fn, index_keys as any);
      },
      paginate: async (page: number, page_size: number, index_to_use: keyof T) => {
         if (!db_name) return []
         return indexeddb_wrapper.paginate<T, any>(
            db_name,
            state_name,
            page,
            page_size,
            index_keys as any,
            index_to_use as string
         );
      },

      count_items_after_key: async (
         filter_fn: (item: T) => boolean,
         last_cursor_key: IDBValidKey,
         index_to_use: keyof T
      ): Promise<number> => {
         if (!db_name) return 0
         return indexeddb_wrapper.count_items_after_key(
            db_name,
            state_name,
            filter_fn,
            index_keys as any,
            last_cursor_key,
            index_to_use,
         )
      },
      paginated_filter: async (
         options: {
            page: number;
            page_size: number;
            filter_fn: (item: T) => boolean;
            index_keys: IndexKeys<T>;
            index_to_use?: Extract<keyof T, string>;
            cursor_key?: IDBValidKey;
            sort_direction: 'asc' | 'desc';
         }) => {
         if (!db_name) return {} as Awaited<ReturnType<typeof indexeddb_wrapper.paginated_filter>>
         return indexeddb_wrapper.paginated_filter(
            db_name,
            state_name,
            options as any,
         );
      },
      count: async () => {
         if (!db_name) return 0
         return indexeddb_wrapper.count(db_name, state_name, index_keys as any);
      }
   };

   function create_paginated_state(page: number = 1, page_size: number = 5): PaginatedState<T> {
      let current_page = $state(page);
      let page_size_internal = $state(page_size);
      let search_fn_internal: FilteringFn<T> | undefined = $state(undefined);
      let global_filter_fn: FilteringFn<T> | undefined = $state(undefined);
      let has_more_pages = $state(false);
      let first_item_index_key = $state<IDBValidKey | undefined>(undefined);
      let last_item_index_key = $state<IDBValidKey | undefined>(undefined);
      let is_search_mode = $state(false);
      let total_count = $state(0);
      let local_order_direction: 'asc' | 'desc' = $state('desc');
      // Monotonic sequence guarding against out-of-order IndexedDB results: a page/count read issued
      // before a hydration write (populate/stream batch) can resolve AFTER fresher reads and must not clobber them.
      let request_version = 0;
      // Separate counter for count reads: a count bump must not invalidate an in-flight
      // page read (and vice versa). Sharing one counter let a concurrent update_total_count
      // drop a freshly-fetched page -> correct footer total but empty rows.
      let count_version = 0;

      function get_derived_item(item: T): T {
         return (derive_value_fn ? derive_value_fn(item) : item)
      };

      function get_effective_filter() {
         return function (item: T) {
            const derived = get_derived_item(item);
            let pass = true;
            if (global_filter_fn) pass = pass && global_filter_fn(derived);
            if (is_search_mode && search_fn_internal) pass = pass && search_fn_internal(derived);
            return pass;
         };
      };

      async function update_total_count() {
         const count_request_seq = ++count_version;
         const total = await indexeddb_crud.paginated_filter_count(get_effective_filter());
         if (count_request_seq !== count_version) return;
         total_count = total;
      };

      async function fetch_page(options: { requested_page: number, cursor_key?: IDBValidKey, sort_direction?: 'asc' | 'desc' }) {
         const { requested_page, cursor_key, sort_direction } = options

         const result = await indexeddb_crud.paginated_filter({
            page: requested_page,
            page_size: page_size_internal,
            filter_fn: get_effective_filter(),
            index_keys: index_keys as any,
            index_to_use: index_keys?.main_index as any,
            cursor_key,
            sort_direction: sort_direction ?? local_order_direction
         });

         const [items, new_first_key, new_last_key, has_more] = result;
         return {
            items: items.map((item) => get_derived_item(item as unknown as T)),
            first_key: new_first_key,
            last_key: new_last_key,
            has_more
         };
      };

      function update_page_state(
         items: T[],
         page: number,
         first_key: IDBValidKey | undefined,
         last_key: IDBValidKey | undefined,
         has_more: boolean,
         sort_direction?: 'asc' | 'desc',
         request_seq?: number
      ) {
         if (request_seq !== undefined && request_seq !== request_version) return;
         paginated_items_state = items as PaginatedT<T>[];
         current_page = page;
         first_item_index_key = first_key;
         last_item_index_key = last_key;
         has_more_pages = has_more;
         if (sort_direction) local_order_direction = sort_direction;
      };


      // Core handlers
      const search_handlers = {
         async start(search_fn: FilteringFn<T>, sort_direction?: 'asc' | 'desc') {
            is_search_mode = true;
            search_fn_internal = search_fn;
            await search_handlers.goto_page(1, sort_direction);
            update_total_count();
         },

         async goto_page(requested_page: number, sort_direction?: 'asc' | 'desc') {
            const request_seq = ++request_version;
            const { items, first_key, last_key, has_more } = await fetch_page({ requested_page, sort_direction });
            update_page_state(items, requested_page, first_key, last_key, has_more, sort_direction, request_seq);
         },

         async next() {
            if (!has_more_pages || !last_item_index_key) return;
            const request_seq = ++request_version;
            const { items, first_key, last_key, has_more } = await fetch_page({ requested_page: current_page + 1, cursor_key: last_item_index_key });
            if (request_seq !== request_version) return;

            if (items.length) {
               update_page_state(items, current_page + 1, first_key, last_key, has_more);
            } else {
               has_more_pages = false;
            }
         },

         async refresh() {
            if (!first_item_index_key) return this.goto_page(1);

            const items_after = await indexeddb_crud.count_items_after_key(
               get_effective_filter(),
               first_item_index_key,
               index_keys?.main_index as any
            );

            const new_page = Math.floor(items_after / page_size_internal) + 1;
            await this.goto_page(new_page);
            update_total_count();
         },

         cancel() {
            is_search_mode = false;
            search_fn_internal = undefined;
            pagination_handlers.start(1, page_size_internal);
         }
      };

      const pagination_handlers = {
         async start(page: number = 1, size: number = page_size_internal, filter_fn?: FilteringFn<T>, sort_direction?: 'asc' | 'desc') {
            page_size_internal = size;
            is_search_mode = false;
            if (filter_fn) global_filter_fn = filter_fn;

            is_pagination_active = true

            await this.goto_page(page, sort_direction);
            update_total_count();
         },

         async goto_page(page: number, sort_direction?: 'asc' | 'desc') {
            const request_seq = ++request_version;
            const { items, first_key, last_key, has_more } = await fetch_page({ requested_page: page, sort_direction });
            update_page_state(items, page, first_key, last_key, has_more, sort_direction, request_seq);
         },

         async next() {
            if (!has_more_pages || !last_item_index_key) return;
            const request_seq = ++request_version;
            const { items, first_key, last_key, has_more } = await fetch_page({ requested_page: current_page + 1, cursor_key: last_item_index_key });
            if (request_seq !== request_version) return;

            if (items.length) {
               update_page_state(items, current_page + 1, first_key, last_key, has_more);
            } else {
               has_more_pages = false;
            }
         },

         async refresh() {
            await this.goto_page(current_page);
            update_total_count();
         },

         cancel() {
            request_version++;
            count_version++;
            is_search_mode = false;
            paginated_items_state = [];
            is_pagination_active = false;

            search_fn_internal = undefined;
            current_page = 1
            has_more_pages = false
            first_item_index_key = undefined
            last_item_index_key = undefined
            global_filter_fn = undefined

            page_size_internal = 5
            total_count = 0
         }
      };

      return {
         search: {
            start: search_handlers.start,
            cancel: search_handlers.cancel,
            goto_page: search_handlers.goto_page,
            next: search_handlers.next,
            prev: () => search_handlers.goto_page(current_page - 1),
            refresh: search_handlers.refresh,
            get is_active() { return is_search_mode; },
            get search_fn() { return search_fn_internal; }

         },
         pagination: {
            start: pagination_handlers.start,
            cancel: pagination_handlers.cancel,
            next: pagination_handlers.next,
            current: pagination_handlers.refresh,
            goto_page: pagination_handlers.goto_page,
            prev: () => pagination_handlers.goto_page(current_page - 1),
         },
         set_global_filter(filter_fn: FilteringFn<T> | undefined) {
            global_filter_fn = filter_fn;
            (is_search_mode ? search_handlers : pagination_handlers).refresh();
            update_total_count();
         },
         refresh: () => {
            if (is_search_mode) search_handlers.refresh()
            else pagination_handlers.refresh()
         },
         get_effective_filter_fn: get_effective_filter,
         get page() { return current_page; },
         get page_size() { return page_size_internal; },
         set page_size(size: number) { page_size_internal = size; },
         get values() { return paginated_items_state; },
         get count() { return total_count; }
      };
   }

   async function get_state_db_count() {
      state_count = await indexeddb_crud.count();
   }

   async function populate_state_from_db() {
      if (incremental) {
         get_state_db_count();
      }
      // indexeddb_crud.keys()
      // .then(keys => {
      // 	keys?.forEach((key) => current_map_state.set(key, null))
      // })
      // .catch(error => console.error('error at init_keys_fn().then: ' + state_name, error)).finally(() => {
      // })
      else {
         get_stored_values()
            .then((values) => {
               values?.forEach((value) => current_map_state.set(value.id, value));
            })
            .catch((error) => console.error('error at init_keys_fn().then: ' + state_name, error))
      }
   }

   if (db_name && is_browser) populate_state_from_db();

   function get_count() {
      return current_map_state.size ? current_map_state.size : state_count;
   }

   let Type = '' as unknown as T;
   let PaginatedType = '' as unknown as PaginatedT<T>;


   function on_updates(fn: (values: T[]) => any) {
      if (!fn) return () => { };
      on_updates_set.add(fn);

      return () => on_updates_set.delete(fn);
   }

   function after_updates(fn: (values: T[]) => any) {
      if (!fn) return () => { };
      after_updates_set.add(fn);

      return () => after_updates_set.delete(fn);

   }

   function on_deletes(fn: (ids: string[]) => any) {
      if (!fn) return () => { };
      on_deletes_set.add(fn);

      return () => on_deletes_set.delete(fn);
   }


   function on_update_single(fn: (value: T) => any) {
      if (!fn) return () => { };
      on_update_single_set.add(fn);

      return () => on_update_single_set.delete(fn);
   }

   function after_update_single(fn: (value: T) => any) {
      if (!fn) return () => { };
      after_update_single_set.add(fn);

      return () => after_update_single_set.delete(fn);

   }

   function on_delete_single(fn: (id: string) => any) {
      if (!fn) return () => { };
      on_delete_single_set.add(fn);

      return () => on_delete_single_set.delete(fn);
   }


   function items_with_sub(filter_fn: (item: T) => boolean = () => true, return_fields?: Array<keyof T>,) {
      const reference_id = graph_utils.random_string()
      let async_values_promise: Promise<T[]> = $state(Promise.resolve([]))

      const map_manager = create_disposable_map<string, T>(() => unsubscribe_handler());

      const values_map = map_manager.instance;
      let unsubscribe: (() => void) | null = $state(null)
      let effective_filter_fn = $state(filter_fn)

      function unsubscribe_handler() {
         console.log('unsubbingggg items_with_sub: ', state_name)
         unsubscribe?.()
         filter_subs_map.delete(reference_id)
         unsubscribe = null
      }

      function init(filter_fn: ((item: T) => boolean), return_fields?: Array<keyof T>,) {
         if (values_map.size) values_map.clear()
         effective_filter_fn = filter_fn

         async_values_promise = filter_async(filter_fn, return_fields).then(values => {
            for (const value of values) values_map.set(value.id, value)
            return values
         })

         if (unsubscribe) unsubscribe_handler()

         unsubscribe = on_updates((values) => {
            for (const value of values) {
               if (filter_fn(value)) values_map.set(value.id, return_fields?.length ? filter_fields(value, return_fields) : value)
               else if (values_map.has(value.id)) values_map.delete(value.id)
            }
         })
      }

      function refresh(filter_fn: (item: T) => boolean, return_fields?: Array<keyof T>,) {
         return init(filter_fn, return_fields)
      }

      init(filter_fn, return_fields)


      filter_subs_map.set(reference_id, () => {
         unsubscribe?.()
         values_map.clear()
         refresh(effective_filter_fn, return_fields)
      })

      return {
         get map() { return values_map; },
         get values() { return Array.from(values_map.values()); },
         refresh,
         get async_values_promise() { return async_values_promise },
         unsubscribe: unsubscribe_handler
      }
   }

   function item_with_sub_single(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: Array<keyof T>,) {
      const reference_id = graph_utils.random_string()

      const item = create_disposable_state<T | null>(null, unsubscribe_handler);

      let unsubscribe: (() => void) | null = $state(null)
      let effective_filter_fn: ((item: T) => boolean) | undefined = $state(undefined)
      let async_value_promise: Promise<T | null> = $state(Promise.resolve(null))


      function unsubscribe_handler() {
         unsubscribe?.()
         item.value = null
         filter_subs_map.delete(reference_id)
         unsubscribe = null
         console.log('unsubbingggg item_with_sub_single: ', state_name)
      }

      function init(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: Array<keyof T>,) {
         if (item.value) item.value = null
         const filter_fn = typeof id_or_filter_fn === 'string' ? (item: T) => item.id === id_or_filter_fn : typeof id_or_filter_fn === 'function' ? id_or_filter_fn : undefined
         if (!filter_fn) return item.dispose()

         effective_filter_fn = filter_fn
         if (unsubscribe) unsubscribe()


         async_value_promise = filter_async(filter_fn, return_fields).then(values => {
            if (values.length) item.value = values[0]
            return values[0]
         })

         unsubscribe = on_update_single((value) => {
            if (filter_fn?.(value)) item.value = return_fields?.length ? filter_fields(value, return_fields) : value
         })

      }

      function refresh(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: Array<keyof T>,) {
         return init(id_or_filter_fn, return_fields)
      }

      init(id_or_filter_fn, return_fields)

      filter_subs_map.set(reference_id, () => {
         unsubscribe?.()
         item.value = null
         refresh(effective_filter_fn, return_fields)
      })


      return {
         get value() { return item.value; },
         refresh,
         get async_value_promise() { return async_value_promise },
         unsubscribe: unsubscribe_handler,
      }
   }


   function items_with_no_sub(filter_fn: (item: T) => boolean = () => true, return_fields?: Array<keyof T>,) {
      let async_values_promise: Promise<T[]> = $state(Promise.resolve([]))

      let values_state = $state<T[]>([])

      async_values_promise = filter_async(filter_fn, return_fields).then(values => {
         console.log({ values })
         values_state = values
         return values
      })

      return {
         get values() { return values_state; },
         get async_values_promise() { return async_values_promise },
      }

   }

   function item_with_no_sub_single(id_or_filter_fn?: string | ((item: T) => boolean), return_fields?: Array<keyof T>,) {

      let item: { value: T | null } = $state({ value: null })
      let async_value_promise: Promise<T | null> = $state(Promise.resolve(null))

      const filter_fn = typeof id_or_filter_fn === 'string' ? (item: T) => item.id === id_or_filter_fn : id_or_filter_fn

      if (filter_fn) async_value_promise = filter_async(filter_fn, return_fields).then(values => {
         if (values.length) item.value = values[0]
         return values[0]
      })

      return {
         get value() { return item.value; },
         get async_value_promise() { return async_value_promise },
      }
   }

   async function filter_async(filter_fn: (item: T) => boolean = () => true, return_fields?: Array<keyof T>) {
      const values = await indexeddb_crud.filter(filter_fn, return_fields);
      return !derive_value_fn ? values : values.map((v) => derive_value_fn(v));
   }


   return {
      filter_async,
      items_with_sub,
      item_with_sub_single,
      item_with_no_sub_single,
      items_with_no_sub,
      resync_state,
      state_name,
      get map() {
         return current_map_state as SvelteMap<string, T>;
      },
      get count() {
         return get_count();
      },
      // get paginated_values() { return paginated_state() },
      Type,
      PaginatedType,
      update_many: update_map_values,
      update_one: update_map_value_one,
      remove_one,
      remove_many,
      on_updates,
      after_updates,
      on_deletes,
      get_or_fetch_one,
      on_update_single,
      after_update_single,
      on_delete_single,
      indexeddb_crud: indexeddb_crud as any,
      get paginated_state() {
         if (!paginated_state) paginated_state = create_paginated_state();
         return paginated_state;
      },
      reset: reset_state,
      get_state_latest_sync,
      get_stored_values,
      init: () => { },
      populate_fn: populate_fn_handler
   };
}

function filter_fields<T>(item: T, return_fields: (keyof T)[] | undefined) {
   if (!return_fields) return item
   const partial_item = {} as any;
   return_fields.forEach((key) => { partial_item[key as keyof typeof partial_item] = item[key]; });
   return partial_item;

}


function create_disposable_state<T>(initial_value: T, cleanup_fn?: () => void) {
   let value = $state(initial_value);

   // We use a simple let variable, NOT $state, to hold the function.
   // This avoids Proxy issues when trying to call it.
   let current_cleanup: (() => void) | null = cleanup_fn ?? null;

   // This effect runs once on mount and registers the cleanup handler
   $effect(() => {
      return () => {
         // This runs when the component is destroyed
         if (current_cleanup) current_cleanup();
         current_cleanup = null;
         value = initial_value;
      };
   });

   return {
      // Getter/Setter for the value so it works like a normal object property
      get value() {
         // console.log('get value')
         return value
      },
      set value(v) { value = v },

      /**
       * Register a new cleanup function (e.g., unsubscribe).
       * IMPORTANT: Automatically runs the *previous* cleanup function if one exists,
       * preventing memory leaks if you subscribe multiple times.
       */
      set_cleanup(fn: () => void) {
         // console.log('set_cleanup')
         if (current_cleanup) current_cleanup();
         current_cleanup = fn;
      },

      /**
       * Manually run the cleanup (optional, mostly for internal logic)
       */
      dispose() {
         // console.log('dispose')

         if (current_cleanup) {
            current_cleanup();
            current_cleanup = null;
         }
      }
   };
}

export function create_disposable_map<K, V>(cleanup_fn?: () => void) {
   const map = new SvelteMap<K, V>();
   let current_cleanup: (() => void) | null = cleanup_fn ?? null;

   $effect(() => {
      return () => {
         if (current_cleanup) current_cleanup();
         current_cleanup = null;
         map.clear();
      };
   });

   return {
      instance: map,
      set_cleanup(fn: () => void) {
         if (current_cleanup) current_cleanup();
         current_cleanup = fn;
      },
      dispose() {
         if (current_cleanup) current_cleanup();
         current_cleanup = null;
         map.clear();
      }
   };
}